import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, FileText, FolderHeart, Mail, Wand2, Compass, Loader2, Copy, Check, Save, Info,
} from "lucide-react";
import { toast } from "sonner";

const prettyKey = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const EMAIL_TYPES = [
  { value: "consultation_confirmation", label: "Consultation confirmation" },
  { value: "after_consultation_recap", label: "After-consultation recap" },
  { value: "document_request", label: "Document request" },
  { value: "partner_introduction", label: "Partner introduction" },
  { value: "family_next_step", label: "Family next-step email" },
  { value: "nurture_followup", label: "Nurture follow-up" },
  { value: "thank_you", label: "Thank you email" },
];

const toStr = (v) => (Array.isArray(v) ? v.join("\n") : v == null ? "" : String(v));

function StructuredEditor({ data, onChange, testid }) {
  if (!data || typeof data !== "object") return null;
  return (
    <div className="space-y-3" data-testid={testid}>
      {Object.entries(data).map(([k, v]) => (
        <div key={k}>
          <Label className="mb-1 block text-[13px] font-medium text-[#1C4358]">{prettyKey(k)}</Label>
          <Textarea
            value={toStr(v)}
            onChange={(e) => onChange(k, Array.isArray(v) ? e.target.value.split("\n") : e.target.value)}
            rows={Array.isArray(v) || toStr(v).length > 80 ? 3 : 1}
            data-testid={`ai-field-${k}`}
          />
        </div>
      ))}
    </div>
  );
}

function AITool({ icon: Icon, title, description, endpoint, outputType, related, emailMode, leads, cases }) {
  const [relSel, setRelSel] = useState("none");
  const [emailType, setEmailType] = useState("after_consultation_recap");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [output, setOutput] = useState(null); // string or object
  const [structured, setStructured] = useState(null);

  const relatedParts = () => {
    if (relSel === "none" || !relSel) return null;
    const [type, id] = relSel.split(":");
    return { type, id };
  };

  const buildBody = () => {
    const rp = relatedParts();
    if (emailMode) return { email_type: emailType, related_type: rp?.type, related_id: rp?.id, context: text || null };
    if (related === "cases") return { case_id: rp?.id };
    if (related === "leads") return { lead_id: rp?.id, text: text || null };
    return { text };
  };

  const run = async () => {
    const body = buildBody();
    if (related === "cases" && !body.case_id) { toast.error("Choose a case first"); return; }
    if (!emailMode && related !== "cases" && !body.lead_id && !body.text) { toast.error("Choose a record or paste some text"); return; }
    setLoading(true); setOutput(null); setStructured(null);
    try {
      const { data } = await api.post(endpoint, body);
      if (outputType === "structured") {
        if (data.structured && typeof data.structured === "object") setStructured(data.structured);
        else setOutput(data.raw || "");
      } else {
        setOutput(data.result || "");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI assist is temporarily unavailable");
    } finally { setLoading(false); }
  };

  const finalText = () => {
    if (structured) return Object.entries(structured).map(([k, v]) => `${prettyKey(k)}:\n${toStr(v)}`).join("\n\n");
    return output || "";
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(finalText()); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const saveNote = async () => {
    const rp = relatedParts();
    if (!rp) { toast.error("Select a lead or case to save this note to"); return; }
    setSaving(true);
    try {
      await api.post("/notes", { related_type: rp.type, related_id: rp.id, body: finalText(), ai_generated: true, visibility: "internal" });
      toast.success("Saved as note");
    } catch { toast.error("Could not save note"); } finally { setSaving(false); }
  };

  const hasOutput = structured || (output != null && output !== "");

  return (
    <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
      <div className="flex items-start gap-3 mb-4">
        <span className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(28,67,88,0.10)" }}><Icon size={20} className="text-[#1C4358]" /></span>
        <div><h3 className="font-display text-lg font-semibold text-[#252221]">{title}</h3><p className="text-sm text-muted-foreground">{description}</p></div>
      </div>

      {/* Inputs */}
      <div className="space-y-3 mb-3">
        {emailMode && (
          <div><Label className="mb-1.5 block text-[13px] font-medium">Email type</Label>
            <Select value={emailType} onValueChange={setEmailType}>
              <SelectTrigger data-testid="ai-email-type"><SelectValue /></SelectTrigger>
              <SelectContent>{EMAIL_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {(related === "leads" || related === "cases" || related === "both") && (
          <div><Label className="mb-1.5 block text-[13px] font-medium">{related === "cases" ? "Case" : related === "leads" ? "Lead" : "Related record"}</Label>
            <Select value={relSel} onValueChange={setRelSel}>
              <SelectTrigger data-testid="ai-related-select"><SelectValue placeholder="Choose..." /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                <SelectItem value="none">— None —</SelectItem>
                {(related === "cases" || related === "both") && cases.map((c) => <SelectItem key={c.id} value={`cases:${c.id}`}>{c.case_number ? `${c.case_number} · ` : ""}{c.title || c.case_name || c.primary_contact_name || "Case"}</SelectItem>)}
                {(related === "leads" || related === "both") && leads.map((l) => <SelectItem key={l.id} value={`leads:${l.id}`}>{l.name || [l.first_name, l.last_name].filter(Boolean).join(" ") || "Lead"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {related !== "cases" && (
          <div><Label className="mb-1.5 block text-[13px] font-medium">{emailMode ? "Extra context (optional)" : related === "leads" ? "Or paste intake / call notes" : "Paste notes to process"}</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Type or paste here..." data-testid="ai-text-input" />
          </div>
        )}
      </div>

      <Button onClick={run} disabled={loading} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="ai-generate-button">
        {loading ? <><Loader2 size={16} className="mr-1.5 animate-spin" /> Generating...</> : <><Sparkles size={16} className="mr-1.5" /> Generate</>}
      </Button>

      {/* Output (editable) */}
      {hasOutput && (
        <div className="mt-4 pt-4 border-t border-[#E6EDF2]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#1C4358]/70 uppercase tracking-wide">Draft — edit before saving</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copy} data-testid="ai-copy-button">{copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />} Copy</Button>
              {(related === "leads" || related === "cases" || related === "both") && (
                <Button size="sm" onClick={saveNote} disabled={saving} className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }} data-testid="ai-save-note-button"><Save size={14} className="mr-1" /> Save as note</Button>
              )}
            </div>
          </div>
          {structured ? (
            <StructuredEditor data={structured} onChange={(k, v) => setStructured((p) => ({ ...p, [k]: v }))} testid="ai-structured-output" />
          ) : (
            <Textarea value={output} onChange={(e) => setOutput(e.target.value)} rows={8} data-testid="ai-text-output" />
          )}
        </div>
      )}
    </Card>
  );
}

export default function AIAssistant() {
  const [leads, setLeads] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    api.get("/leads", { params: { limit: 200 } }).then((r) => setLeads(r.data.items || [])).catch(() => {});
    api.get("/cases", { params: { limit: 200 } }).then((r) => setCases(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div>
      <div className="mb-4">
        <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><Sparkles size={22} className="text-[#1C4358]" /> AI Assistant</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Speed up your work with AI drafts. Every result is editable before you save.</p>
      </div>

      <div className="rounded-xl border border-[#9BB9CE] bg-[rgba(155,185,206,0.12)] p-3 mb-5 flex items-start gap-2.5" data-testid="ai-disclaimer">
        <Info size={16} className="text-[#1C4358] mt-0.5 shrink-0" />
        <p className="text-sm text-[#1C4358]">AI results are a <strong>staff aid only</strong> — they may be incomplete or incorrect and do <strong>not</strong> replace professional judgment. Always review and edit before sending or saving. This is not medical, legal, financial, tax, or real-estate advice.</p>
      </div>

      <Tabs defaultValue="intake">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="intake" data-testid="tab-intake"><FileText size={15} className="mr-1.5" /> Intake Summary</TabsTrigger>
          <TabsTrigger value="case" data-testid="tab-case"><FolderHeart size={15} className="mr-1.5" /> Case Summary</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email"><Mail size={15} className="mr-1.5" /> Email Drafts</TabsTrigger>
          <TabsTrigger value="cleanup" data-testid="tab-cleanup"><Wand2 size={15} className="mr-1.5" /> Note Cleanup</TabsTrigger>
          <TabsTrigger value="lane" data-testid="tab-lane"><Compass size={15} className="mr-1.5" /> Lane Recommendation</TabsTrigger>
        </TabsList>

        <TabsContent value="intake" className="mt-4 max-w-2xl">
          <AITool icon={FileText} title="AI Intake Summary" description="Turn lead notes or call notes into a clear summary, urgency, and next steps."
            endpoint="/ai/intake-summary" outputType="structured" related="leads" leads={leads} cases={cases} />
        </TabsContent>
        <TabsContent value="case" className="mt-4 max-w-2xl">
          <AITool icon={FolderHeart} title="AI Case Summary" description="Summarize a family case: situation, stage, obstacles, partners, and next-step checklist."
            endpoint="/ai/case-summary" outputType="structured" related="cases" leads={leads} cases={cases} />
        </TabsContent>
        <TabsContent value="email" className="mt-4 max-w-2xl">
          <AITool icon={Mail} title="AI Follow-Up Email Draft" description="Draft a warm, ready-to-send email for any stage of the journey."
            endpoint="/ai/draft-email" outputType="text" related="both" emailMode leads={leads} cases={cases} />
        </TabsContent>
        <TabsContent value="cleanup" className="mt-4 max-w-2xl">
          <AITool icon={Wand2} title="AI Note Cleanup" description="Convert messy, shorthand notes into a clean, structured CRM note."
            endpoint="/ai/cleanup-note" outputType="text" related="both" leads={leads} cases={cases} />
        </TabsContent>
        <TabsContent value="lane" className="mt-4 max-w-2xl">
          <AITool icon={Compass} title="AI Lane Recommendation" description="Suggest primary/secondary service lanes, urgency, coordinator, and revenue ideas."
            endpoint="/ai/lane-recommendation" outputType="structured" related="leads" leads={leads} cases={cases} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
